import React, { useMemo, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  withStyles,
  withMobile,
  withOverlay,
  withModal,
  withBreakpoint,
  InputContainer,
  Input,
  Calendar,
} from "components/common";
import InfiniteScroll from "react-infinite-scroller";
import Table from "components/common/Table";
import { useApolloClient } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import useMount from "utils/hooks/useMount";
import sortDate from "utils/sortDate";
import { track } from "utils/analytics";
import formatCurrency from "utils/formatCurrency";
import { ACTIVITY_STATUS } from "utils/constants";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const [red, green, blue, gray] = ["#FF8B89", "#60C8B1", "#289EFC", "#cbd5e0"];
const PENDING_ACTIVITY_TYPES = [ACTIVITY_STATUS.PENDING];
const SETTLED_ACTIVITY_TYPES = [
  ACTIVITY_STATUS.SETTLED,
  ACTIVITY_STATUS.RETURNED,
];

const descriptionText = (data) => data.description;

const computeColumns = (isBelowSm) =>
  isBelowSm
    ? [
        {
          Header: "Description",
          accessor: (data) => (
            <div className="flex items-center leading-tight">
              <div
                className={`flex flex-col items-center mr-2 text-sm md:text-base md:mr-0 border-r border-gray-200 pr-2 
                  ${data.returned_at ? "" : "text-gray-500"}`}
              >
                <div>{months[new Date(data.created_at).getMonth()]}</div>
                <div>{new Date(data.created_at).getDate()}</div>
              </div>
              <div className="flex-1 ml-1 md:ml-0">
                <div>
                  {data.disputed ? (
                    <i className="fas fa-exclamation-triangle text-red-500 mr-2 text-sm" />
                  ) : (
                    <></>
                  )}
                  {descriptionText(data)}
                </div>
                <div
                  className={`${data.returned_at ? "" : "text-gray-500"} 
                    text-sm md:text-base`}
                >
                  {data.category ||
                    (`${data.amount || ""}`.startsWith("-") ? "credit" : "")}
                </div>
              </div>
            </div>
          ),
        },
        {
          Header: "Amount",
          style: { paddingTop: 0, paddingBottom: 0 }, // avoid expanding the row when this cell is taller
          accessor: (data) => {
            const t = (data.type || "").toLowerCase();
            const c = (data.category || "").toLowerCase();
            const a = `${data.amount || ""}`;
            const s = `${data.status || ""}`.toLowerCase();
            return (
              <div
                style={{
                  color: (() => {
                    if (t === "payment") {
                      return data.returned_at ? "" : green;
                    }
                    if (t === "refund" || c === "refund" || a.startsWith("-"))
                      return blue;
                    if (s === "disputed") return gray;
                    return "";
                  })(),
                }}
              >
                {`${data.returned_at ? "Failed/Returned " : ""}${
                  data.amount ? formatCurrency(data.amount.toFixed(2)) : ""
                }`}
                {(() => {
                  if (s === "pending") {
                    return (
                      <div className="font-normal text-gray-500 text-xxs">
                        pending
                      </div>
                    );
                  }
                  if (s === "canceled") {
                    return (
                      <div
                        style={{ color: red }}
                        className="font-normal text-xxs"
                      >
                        canceled
                      </div>
                    );
                  }
                  return "";
                })()}
              </div>
            );
          },
        },
      ]
    : [
        {
          Header: "Date",
          style: { width: "200px" },
          accessor: (data) =>
            new Date(data.created_at).toLocaleString("en-US", {
              dateStyle: "medium",
            }),
        },
        {
          Header: "Description",
          accessor: (data) => (
            <div>
              {data.disputed ? (
                <i className="fas fa-exclamation-triangle text-red-500 mr-2 text-sm" />
              ) : (
                <></>
              )}
              {descriptionText(data)}
            </div>
          ),
        },
        {
          Header: "Category",
          accessor: (data) => {
            if (`${data.amount || ""}`.startsWith("-"))
              return data.category || "credit";
            return data.category || "";
          },
        },
        {
          Header: "Amount",
          style: { paddingTop: 0, paddingBottom: 0 }, // avoid expanding the row when this cell is taller
          accessor: (data) => {
            // If we want to make payment amounts negative, add the below to the string:
            // ${(data.description || "").toLowerCase() === "payment" ? "-" : "" }
            return `${
              data.amount ? formatCurrency(data.amount.toFixed(2)) : ""
            }`;
          },
          Cell: (data) => {
            const t = (data.row.original.type || "").toLowerCase();
            const c = (data.row.original.category || "").toLowerCase();
            const a = `${data.row.original.amount || ""}`;
            const s = `${data.row.original.status || ""}`.toLowerCase();
            return (
              <div>
                <div
                  className="flex justify-between"
                  style={{
                    color: (() => {
                      if (t === "payment") {
                        return data.row.original.returned_at ? "" : green;
                      }
                      if (t === "refund" || c === "refund" || a.startsWith("-"))
                        return blue;
                      if (s === "disputed") return gray;
                      return "";
                    })(),
                  }}
                >
                  <span>{`${
                    data.row.original.returned_at ? "Failed/Returned " : ""
                  }`}</span>
                  <span>{data.row.values.Amount}</span>
                </div>
                {(() => {
                  if (s === "pending") {
                    return (
                      <div className="font-normal text-gray-500 text-xxs">
                        pending
                      </div>
                    );
                  }
                  if (s === "canceled") {
                    return (
                      <div
                        style={{ color: red }}
                        className="font-normal text-xxs"
                      >
                        canceled
                      </div>
                    );
                  }
                  return "";
                })()}
              </div>
            );
          },
        },
      ];

const TransactionsTable = ({
  mobileContext: { isMobile },
  overlayContext: { openOverlay },
  modalContext: { openModal },
  breakpointContext: { isBelow },
  userId,
  initialTransactions,
  activityQuery,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [calendarError, setCalendarError] = useState([]);
  const [activityLength, setActivityLength] = useState(0); // used to determine when to show empty list UI state
  const [pendingActivity, setPendingActivity] = useState([]);
  const [settledActivity, setSettledActivity] = useState([]);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const columns = useMemo(() => computeColumns(isBelow("sm")), [isBelow]);
  const client = useApolloClient();

  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [page, setPage] = useState(0);

  useMount(() => {
    client.query({ query: USER_NAME_QUERY }).then((x) => {
      const { data: { user } = {} } = x || {};
      const { first_name: firstName, last_name: lastName } = user || {};
      setUserData({ id: userId, firstName, lastName });
    });
  });

  const loadMore = () => {
    if (loading) {
      return;
    }
    setLoading(true);
    client
      .query({
        query: activityQuery,
        fetchPolicy: "network-only",
        variables: {
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(searchInput && { searchStr: searchInput }),
          page,
        },
      })
      .then((x) => {
        const {
          data: { activity: a = [] },
        } = x;
        setLoading(false);

        if (a.length) {
          setPage(page + 1);
          setActivityLength(a.length); // only set here as length is non-filtered by user's search keywords

          setPendingActivity(
            pendingActivity.concat(
              ...a.filter((i) =>
                PENDING_ACTIVITY_TYPES.includes(
                  i.status && i.status.toUpperCase()
                )
              )
            )
          );

          setSettledActivity(
            settledActivity.concat(
              ...a.filter((i) =>
                SETTLED_ACTIVITY_TYPES.includes(
                  i.status && i.status.toUpperCase()
                )
              )
            )
          );
        } else {
          setHasMore(false);
        }
      });
  };

  const loader = () => <div className="loader m-auto h-16 w-16" key={0} />;

  // NOTE: Good transactions might be legacy Synpase-era code as it seemed undefined.
  const goodPendingTransactions = initialTransactions.filter((a) =>
    PENDING_ACTIVITY_TYPES.includes(a.status)
  );
  const goodSettledTransactions = initialTransactions.filter((a) =>
    SETTLED_ACTIVITY_TYPES.includes(a.status)
  );

  return (
    <InfiniteScroll
      pageStart={page}
      loadMore={loadMore}
      hasMore={hasMore}
      loader={loader()}
    >
      <div>
        <div className="mb-2">
          <InputContainer className="flex flex-1 w-full">
            <div className="flex justify-between w-full">
              <Input
                placeholder="Search"
                id="transactions-search"
                value={searchInput}
                onChange={({ target: { value } }) => {
                  setSearchInput(value);
                  setLoading(true);
                  setPage(0);
                  setHasMore(true);
                  setPendingActivity([]);
                  setSettledActivity([]);
                  client
                    .query({
                      query: activityQuery,
                      variables: {
                        searchStr: value,
                        fromDate,
                        toDate,
                        page: 0,
                      },
                    })
                    .then((res) => {
                      track("DASHBOARD_TRANSACTIONS_FILTER_APPLIED");
                      const { data: { activity = [] } = {} } = res || {};
                      setPendingActivity(
                        activity.filter((a) =>
                          PENDING_ACTIVITY_TYPES.includes(a.status)
                        )
                      );
                      setSettledActivity(
                        activity.filter((a) =>
                          SETTLED_ACTIVITY_TYPES.includes(a.status)
                        )
                      );
                      setPage(1);
                      setLoading(false);
                    });
                }}
              />
              <i className="fas fa-search text-gray-secondary self-center" />
            </div>
          </InputContainer>
        </div>
        <div className="flex flex-col w-full sm:flex-row">
          <Calendar
            onCalendarError={(err) => {
              console.log("calendar error", err);
              setCalendarError(err);
            }}
            onCalendarChange={(from, to) => {
              const newFromDate = from && from.toDate();
              const newToDate = to && to.toDate();
              setFromDate(newFromDate);
              setToDate(newToDate);
              setLoading(true);
              setPage(0);
              setHasMore(true);
              setPendingActivity([]);
              setSettledActivity([]);
              client
                .query({
                  query: activityQuery,
                  variables: {
                    fromDate: newFromDate,
                    toDate: newToDate,
                    searchStr: searchInput,
                    page: 0,
                  },
                })
                .then((res) => {
                  track("DASHBOARD_TRANSACTIONS_FILTER_APPLIED");
                  const { data: { activity = [] } = {} } = res || {};
                  setPendingActivity(
                    activity.filter((a) =>
                      PENDING_ACTIVITY_TYPES.includes(a.status)
                    )
                  );
                  setSettledActivity(
                    activity.filter((a) =>
                      SETTLED_ACTIVITY_TYPES.includes(a.status)
                    )
                  );
                  setPage(1);
                  setLoading(false);
                  track("DASHBOARD_TRANSACTIONS_FILTER_APPLIED");
                });
            }}
          />
        </div>
        {!!calendarError.length &&
          calendarError.map(({ message }) => <div>{message}</div>)}
        <div className="my-6">
          {showEmpty(activityLength, pendingActivity, settledActivity)}
          {showPending(
            pendingActivity,
            settledActivity,
            goodPendingTransactions,
            columns,
            isMobile,
            openOverlay,
            openModal,
            userData
          )}
          {showSettled(
            settledActivity,
            goodSettledTransactions,
            columns,
            isMobile,
            openOverlay,
            openModal,
            userData
          )}
        </div>
      </div>
    </InfiniteScroll>
  );
};

function showEmpty(activityLength, pendingActivity, settledActivity) {
  if (activityLength === 0) {
    return (
      <>
        <p>
          You have no activities right now. Start using your World Elite card
          today!
        </p>
      </>
    );
  }
  if (pendingActivity.length === 0 && settledActivity.length === 0) {
    return (
      <>
        <p>No search results</p>
      </>
    );
  }
}

function showPending(
  pendingActivity,
  settledActivity,
  goodPendingTransactions,
  columns,
  isMobile,
  openOverlay,
  openModal,
  userData
) {
  const totalPendingActivityCount = pendingActivity.length;
  const totalPendingActivityVolume = pendingActivity.reduce(
    (sum, activity) => Number(activity.amount) + Number(sum),
    0
  );

  const PendingTableFooter = () => {
    return (
      <tr className="bg-green-secondary">
        <td colSpan={columns.length - 1} className="text-right">
          Total Pending Transactions ({totalPendingActivityCount})
        </td>
        <td className="font-medium">
          {formatCurrency(totalPendingActivityVolume) || "$0.00"}
        </td>
      </tr>
    );
  };

  if (pendingActivity.length > 0) {
    return (
      <>
        <h3 className="font-title mb-2 text-xl">Pending Transactions</h3>
        <Table
          id="pending-transactions-table"
          data={pendingActivity || sortDate(goodPendingTransactions)}
          columns={columns}
          getRowStyle={getRowStyle}
          onRowClick={(e, row) =>
            rowOnClickHandler(
              e,
              row,
              isMobile,
              openOverlay,
              openModal,
              userData
            )
          }
          TableFooterComponent={PendingTableFooter}
        />

        {settledActivity.length > 0 ? <br /> : null}
      </>
    );
  }
}

const getRowStyle = (row) => {
  return {
    backgroundColor: row.original.returned_at ? red : "",
  };
};

function showSettled(
  settledActivity,
  goodSettledTransactions,
  columns,
  isMobile,
  openOverlay,
  openModal,
  userData
) {
  if (settledActivity.length > 0) {
    return (
      <>
        <h3 className="font-title mb-2 text-xl">Settled Transactions</h3>
        <Table
          id="settled-transactions-table"
          data={settledActivity || sortDate(goodSettledTransactions)}
          columns={columns}
          getRowStyle={getRowStyle}
          onRowClick={(e, row) =>
            rowOnClickHandler(
              e,
              row,
              isMobile,
              openOverlay,
              openModal,
              userData
            )
          }
        />
      </>
    );
  }
}

function rowOnClickHandler(e, row, isMobile, openOverlay, openModal, userData) {
  const { original: { type } = {} } = row;
  if (type !== "payment") {
    if (isMobile) {
      openOverlay(e, "DisputeOverlay", {
        row,
        user: userData,
      });
    } else {
      openModal(e, "DisputeModal", {
        row,
        user: userData,
      });
    }
  }
}

const USER_NAME_QUERY = gql`
  query UserName {
    user {
      first_name
      last_name
    }
  }
`;

export default compose(
  connect((state) => ({
    userId: state.user.id,
  })),
  withMobile,
  withStyles,
  withOverlay,
  withModal,
  withBreakpoint
)(TransactionsTable);
